import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Select, Space, DatePicker, message, Segmented, InputNumber, Popconfirm } from 'antd';
import {
  DownloadOutlined,
  SearchOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { makeGetCall, makePostCall } from '../../utils/helper';
import dayjs from 'dayjs';
import './cost-reports.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const CostReports = () => {
  const [reportData, setReportData] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [dateRange, setDateRange] = useState(null);
  const [centreFilter, setCentreFilter] = useState(null);
  const [pricingTab, setPricingTab] = useState('all');

  // Inline edit state: { rowKey: priceValue }
  const [inlinePrices, setInlinePrices] = useState({});
  const [savingRow, setSavingRow] = useState(null);

  // Dropdown data
  const [centres, setCentres] = useState([]);

  useEffect(() => {
    const fetchCentres = async () => {
      try {
        const res = await makeGetCall('/centres?active=true');
        if (res.data.success) {
          setCentres(res.data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch centres:', err);
      }
    };
    fetchCentres();
  }, []);

  const handleDatePreset = (preset) => {
    const today = dayjs();
    switch (preset) {
      case 'today':
        setDateRange([today.startOf('day'), today]);
        break;
      case 'this_week':
        setDateRange([today.startOf('week'), today]);
        break;
      case 'this_month':
        setDateRange([today.startOf('month'), today]);
        break;
      case 'last_month':
        setDateRange([
          today.subtract(1, 'month').startOf('month'),
          today.subtract(1, 'month').endOf('month'),
        ]);
        break;
      default:
        break;
    }
  };

  const handleGenerateReport = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning('Please select a date range');
      return;
    }

    setLoading(true);
    setError(null);
    setInlinePrices({});

    try {
      const response = await makePostCall('/centre-cost-report', {
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
        centre_code: centreFilter || null,
      });

      if (response.data.success) {
        setReportData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to generate report');
      }
    } catch (err) {
      setError(err.message || 'Failed to generate report');
      message.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // Filtered rows based on pricing tab
  const filteredRows = useMemo(() => {
    if (!reportData.rows) return [];
    if (pricingTab === 'priced') return reportData.rows.filter((r) => r.unit_price !== null);
    if (pricingTab === 'unpriced') return reportData.rows.filter((r) => r.unit_price === null);
    return reportData.rows;
  }, [reportData.rows, pricingTab]);

  // Recomputed summary based on filtered rows + inline edits
  const displaySummary = useMemo(() => {
    if (!reportData.summary) return null;

    let total_studies = 0;
    let total_cost = 0;
    let unpriced_studies = 0;

    for (const row of filteredRows) {
      const rowKey = `${row.centre_code}-${row.modality}-${row.body_part}`;
      const inlinePrice = inlinePrices[rowKey];
      const effectivePrice = row.unit_price !== null ? parseFloat(row.unit_price) : (inlinePrice ?? null);

      total_studies += row.study_count;
      if (effectivePrice !== null) {
        total_cost += row.study_count * effectivePrice;
      } else {
        unpriced_studies += row.study_count;
      }
    }

    return {
      total_studies,
      total_cost: parseFloat(total_cost.toFixed(2)),
      unpriced_studies,
    };
  }, [filteredRows, inlinePrices, reportData.summary]);

  // Save inline price as a pricing rule
  const handleSavePricingRule = async (record) => {
    const rowKey = `${record.centre_code}-${record.modality}-${record.body_part}`;
    const price = inlinePrices[rowKey];

    if (price === undefined || price === null || price < 0) {
      message.warning('Please enter a valid price before saving');
      return;
    }

    setSavingRow(rowKey);
    try {
      const response = await makePostCall('/centre-pricing', {
        cp_centre_code: record.centre_code,
        cp_modality: record.modality,
        cp_body_part: record.body_part,
        cp_price: price,
        cp_active: true,
      });

      if (response.data.success) {
        message.success(`Pricing rule saved for ${record.centre_code} / ${record.modality} / ${record.body_part}`);
        // Update the row in-place to reflect the saved price
        setReportData((prev) => ({
          ...prev,
          rows: prev.rows.map((r) => {
            if (r.centre_code === record.centre_code && r.modality === record.modality && r.body_part === record.body_part) {
              return {
                ...r,
                unit_price: price,
                total_cost: (r.study_count * price).toFixed(2),
              };
            }
            return r;
          }),
          summary: {
            ...prev.summary,
            unpriced_studies: prev.summary.unpriced_studies - record.study_count,
            total_cost: parseFloat((prev.summary.total_cost + record.study_count * price).toFixed(2)),
          },
        }));
        // Clear inline edit for this row
        setInlinePrices((prev) => {
          const next = { ...prev };
          delete next[rowKey];
          return next;
        });
      } else {
        throw new Error(response.data.message || 'Failed to save pricing rule');
      }
    } catch (err) {
      message.error(err.message || 'Failed to save pricing rule');
    } finally {
      setSavingRow(null);
    }
  };

  const handleExportCSV = () => {
    if (!filteredRows || filteredRows.length === 0) {
      message.warning('No data to export');
      return;
    }

    const headers = ['Centre Name', 'Centre Code', 'Modality', 'Body Part', 'Study Count', 'Unit Price', 'Total Cost'];
    const csvRows = [headers.join(',')];

    for (const row of filteredRows) {
      csvRows.push([
        `"${(row.centre_name || '').replace(/"/g, '""')}"`,
        `"${row.centre_code}"`,
        `"${row.modality}"`,
        `"${(row.body_part || '').replace(/"/g, '""')}"`,
        row.study_count,
        row.unit_price !== null ? parseFloat(row.unit_price).toFixed(2) : 'Unpriced',
        parseFloat(row.total_cost).toFixed(2),
      ].join(','));
    }

    if (displaySummary) {
      csvRows.push('');
      csvRows.push(`"Summary",,,,${displaySummary.total_studies},,${displaySummary.total_cost.toFixed(2)}`);
      csvRows.push(`"Unpriced Studies",,,,${displaySummary.unpriced_studies},,`);
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = dateRange ? `${dateRange[0].format('YYYY-MM-DD')}_to_${dateRange[1].format('YYYY-MM-DD')}` : 'report';
    link.download = `centre_cost_report_${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: 'Centre Name',
      dataIndex: 'centre_name',
      key: 'centre_name',
      sorter: (a, b) => (a.centre_name || '').localeCompare(b.centre_name || ''),
    },
    {
      title: 'Centre Code',
      dataIndex: 'centre_code',
      key: 'centre_code',
    },
    {
      title: 'Modality',
      dataIndex: 'modality',
      key: 'modality',
      sorter: (a, b) => (a.modality || '').localeCompare(b.modality || ''),
    },
    {
      title: 'Body Part',
      dataIndex: 'body_part',
      key: 'body_part',
      sorter: (a, b) => (a.body_part || '').localeCompare(b.body_part || ''),
    },
    {
      title: 'Study Count',
      dataIndex: 'study_count',
      key: 'study_count',
      sorter: (a, b) => a.study_count - b.study_count,
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      render: (price, record) => {
        if (price !== null) {
          return parseFloat(price).toFixed(2);
        }
        const rowKey = `${record.centre_code}-${record.modality}-${record.body_part}`;
        return (
          <InputNumber
            size="small"
            min={0}
            step={0.01}
            precision={2}
            placeholder="Set price"
            value={inlinePrices[rowKey]}
            onChange={(val) => setInlinePrices((prev) => ({ ...prev, [rowKey]: val }))}
            style={{ width: 110 }}
          />
        );
      },
      sorter: (a, b) => (parseFloat(a.unit_price) || 0) - (parseFloat(b.unit_price) || 0),
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      render: (cost, record) => {
        if (record.unit_price !== null) {
          return parseFloat(cost).toFixed(2);
        }
        const rowKey = `${record.centre_code}-${record.modality}-${record.body_part}`;
        const inlinePrice = inlinePrices[rowKey];
        if (inlinePrice !== undefined && inlinePrice !== null) {
          return <span style={{ color: '#1890ff', fontWeight: '300' }}>{(record.study_count * inlinePrice).toFixed(2)}</span>;
        }
        return <span style={{ color: '#faad14' }}>--</span>;
      },
      sorter: (a, b) => parseFloat(a.total_cost) - parseFloat(b.total_cost),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        if (record.unit_price !== null) return null;
        const rowKey = `${record.centre_code}-${record.modality}-${record.body_part}`;
        const hasPrice = inlinePrices[rowKey] !== undefined && inlinePrices[rowKey] !== null;
        if (!hasPrice) return null;
        return (
          <Popconfirm
            title="Save as pricing rule?"
            description={`${record.centre_code} / ${record.modality} / ${record.body_part} @ ${inlinePrices[rowKey]}`}
            onConfirm={() => handleSavePricingRule(record)}
            okText="Save"
            cancelText="Cancel"
          >
            <Button
              type="link"
              size="small"
              icon={<SaveOutlined />}
              loading={savingRow === rowKey}
              title="Save as pricing rule"
            >
              Save
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  // Summary footer row
  const summaryFooter = () => {
    if (!displaySummary || filteredRows.length === 0) return null;
    return (
      <Table.Summary fixed>
        <Table.Summary.Row className="summary-footer-row">
          <Table.Summary.Cell index={0} colSpan={4}>
            <strong>Grand Total</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4}>
            <strong>{displaySummary.total_studies}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} />
          <Table.Summary.Cell index={6}>
            <strong>{displaySummary.total_cost.toFixed(2)}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7} />
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const unpricedCount = reportData.rows ? reportData.rows.filter((r) => r.unit_price === null).length : 0;

  return (
    <div className="admin-container cost-reports-container">
      <div className="admin-header">
        <h2>Cost Reports</h2>
      </div>

      <div className="filter-bar">
        <Space size="middle" wrap>
          <Button size="small" onClick={() => handleDatePreset('today')}>Today</Button>
          <Button size="small" onClick={() => handleDatePreset('this_week')}>This Week</Button>
          <Button size="small" onClick={() => handleDatePreset('this_month')}>This Month</Button>
          <Button size="small" onClick={() => handleDatePreset('last_month')}>Last Month</Button>

          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="YYYY-MM-DD"
          />

          <Select
            placeholder="All Centres"
            value={centreFilter}
            onChange={setCentreFilter}
            style={{ width: 200 }}
            allowClear
          >
            {centres.map((c) => (
              <Option key={c.code} value={c.code}>
                {c.name} ({c.code})
              </Option>
            ))}
          </Select>

          {dateRange && dateRange.length === 2 && (
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleGenerateReport}
              loading={loading}
            >
              Generate Report
            </Button>
          )}
        </Space>
      </div>

      {displaySummary && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-label">Total Studies</div>
            <div className="summary-value">{displaySummary.total_studies}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Cost</div>
            <div className="summary-value">{displaySummary.total_cost.toFixed(2)}</div>
          </div>
          <div className={`summary-card ${displaySummary.unpriced_studies > 0 ? 'warning' : ''}`}>
            <div className="summary-label">Unpriced Studies</div>
            <div className="summary-value">{displaySummary.unpriced_studies}</div>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <Button onClick={handleGenerateReport}>Retry</Button>
        </div>
      )}

      {reportData.rows.length > 0 && (
        <div className="admin-table-container">
          <div className="table-toolbar">
            <Segmented
              value={pricingTab}
              onChange={setPricingTab}
              options={[
                { label: `All (${reportData.rows.length})`, value: 'all' },
                { label: `Priced (${reportData.rows.length - unpricedCount})`, value: 'priced' },
                { label: `Unpriced (${unpricedCount})`, value: 'unpriced' },
              ]}
            />
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleExportCSV}>
              Export CSV
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={filteredRows}
            loading={loading}
            rowKey={(record, index) => `${record.centre_code}-${record.modality}-${record.body_part}-${index}`}
            rowClassName={(record) => (record.unit_price === null ? 'unpriced-row' : '')}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} rows`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            scroll={{ x: 'max-content' }}
            summary={() => summaryFooter()}
            size="large"
          />
        </div>
      )}
    </div>
  );
};

export default CostReports;
